"use client";

import { Table } from "@tanstack/react-table";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RepostAllOrdersDialog } from "@/components/admin/SumOrdersTable/RepostAllOrdersDialog";
import { DataTableViewOptions } from "@/components/admin/UserTable/data-table-view-options";
import { EtpaOrder } from "@/client-admin/types.gen";
import { getUniqueIntradayCodesFromEtpaOrders, orderStatuses, orderTypes, xbidOrderTypes } from "./columns";
import { DataTableFacetedFilter } from "./data-table-faceted-filter";
import { DeleteEtpaOrdersDialog } from "./DeleteEtpaOrdersDialog";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  data: TData[];
}

export function DataTableToolbar<TData>({ table, data }: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const uniqueIntradayCodes = getUniqueIntradayCodesFromEtpaOrders(data as EtpaOrder[]);
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedIds = selectedRows.map((row) => (row.original as EtpaOrder).id) as string[];

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        {selectedRows.length > 0 && (
          <DeleteEtpaOrdersDialog orderIds={selectedIds} selectedCount={selectedRows.length} table={table} />
        )}
        <RepostAllOrdersDialog />
        <Input
          placeholder="Filter by ID..."
          value={(table.getColumn("id")?.getFilterValue() as string) ?? ""}
          onChange={(event) => table.getColumn("id")?.setFilterValue(event.target.value)}
          className="h-8 w-[150px] lg:w-[250px]"
        />
        {table.getColumn("intradayCode") && (
          <DataTableFacetedFilter
            column={table.getColumn("intradayCode")}
            title="Intraday Code"
            options={uniqueIntradayCodes}
          />
        )}
        {table.getColumn("orderType") && (
          <DataTableFacetedFilter column={table.getColumn("orderType")} title="Order Type" options={orderTypes} />
        )}
        {table.getColumn("orderStatus") && (
          <DataTableFacetedFilter column={table.getColumn("orderStatus")} title="Status" options={orderStatuses} />
        )}
        {table.getColumn("xbidOrderType") && (
          <DataTableFacetedFilter
            column={table.getColumn("xbidOrderType")}
            title="XBID Type"
            options={xbidOrderTypes}
          />
        )}
        {isFiltered && (
          <Button variant="ghost" onClick={() => table.resetColumnFilters()} className="h-8 px-2 lg:px-3">
            Reset
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}
